import React, { useState } from 'react';
import { PitchDeck, PitchDeckSlide } from '../../types/pitchDeck';
import SlidePreview from './SlidePreview';

interface SlideEditorProps {
  slide: PitchDeckSlide;
  theme: PitchDeck['theme'];
  isGenerating: boolean;
  onSlideUpdate?: (updatedSlide: PitchDeckSlide) => void;
  onSlideGenerate?: (slideId: string) => void;
}

const SlideEditor: React.FC<SlideEditorProps> = ({ 
  slide, 
  theme,
  isGenerating,
  onSlideUpdate,
  onSlideGenerate
}) => {
  // State for the edited slide content
  const [title, setTitle] = useState(slide.title);
  const [content, setContent] = useState(slide.content);
  const [imageUrl, setImageUrl] = useState(slide.imageUrl || '');
  const [layout, setLayout] = useState(slide.style.layout || 'centered');
  const [backgroundColor, setBackgroundColor] = useState(slide.style.backgroundColor || theme.primaryColor);
  const [textColor, setTextColor] = useState(slide.style.textColor || '#ffffff');

  // Props for slide updates
  const updateSlide = (updatedSlide: Partial<PitchDeckSlide>) => {
    // This would be handled by parent component through props
    if (onSlideUpdate) {
      onSlideUpdate({...slide, ...updatedSlide});
    }
  };
  
  const generateSlide = async (slideId: string) => {
    // This would be handled by parent component through props
    if (onSlideGenerate) {
      onSlideGenerate(slideId);
    }
  };

  // Timer for auto-saving changes after user stops typing
  const [saveTimeout, setSaveTimeout] = useState<NodeJS.Timeout | null>(null);

  // Save changes when user stops typing
  const handleChange = (field: string, value: string) => {
    // Clear previous timeout
    if (saveTimeout) {
      clearTimeout(saveTimeout);
    }

    // Set new field value based on the field name
    switch (field) {
      case 'title':
        setTitle(value);
        break;
      case 'content':
        setContent(value);
        break;
      case 'imageUrl':
        setImageUrl(value);
        break;
      case 'layout':
        setLayout(value as any);
        break;
      case 'backgroundColor':
        setBackgroundColor(value);
        break;
      case 'textColor':
        setTextColor(value);
        break;
    }

    // Set a new timeout to save changes after 500ms of inactivity
    const timeout = setTimeout(() => {
      saveChanges({
        title: field === 'title' ? value : title,
        content: field === 'content' ? value : content,
        imageUrl: field === 'imageUrl' ? value : imageUrl,
        style: {
          ...slide.style,
          layout: field === 'layout' ? value as any : layout,
          backgroundColor: field === 'backgroundColor' ? value : backgroundColor,
          textColor: field === 'textColor' ? value : textColor,
        }
      });
    }, 500);

    setSaveTimeout(timeout);
  };

  // Save changes to the store
  const saveChanges = async (updates: Partial<PitchDeckSlide>) => {
    if (onSlideUpdate) {
      const updatedSlide = { ...slide, ...updates };
      onSlideUpdate(updatedSlide);
    }
  };

  // Handle AI generation of slide content
  const handleGenerateContent = async () => {
    if (!title || !onSlideGenerate) return;
    
    try {
      onSlideGenerate(slide.id);
    } catch (error) {
      console.error('Error generating slide content:', error);
    }
  };

  return (
    <div className="flex h-full">
      {/* Editor controls */}
      <div className="w-1/3 p-4 border-r border-gray-200 overflow-y-auto">
        <h3 className="font-medium mb-4">Edit Slide</h3>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Slide Title
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => handleChange('title', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Content
          </label>
          <textarea
            value={content}
            onChange={(e) => handleChange('content', e.target.value)}
            rows={6}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          
          <button
            onClick={handleGenerateContent}
            disabled={isGenerating || !title}
            className={`mt-2 text-sm ${
              isGenerating || !title
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700'
            } text-white py-1 px-3 rounded`}
          >
            {isGenerating ? 'Generating...' : 'Generate with AI'}
          </button>
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Image URL
          </label>
          <input
            type="text"
            value={imageUrl}
            onChange={(e) => handleChange('imageUrl', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder="https://example.com/image.jpg"
          />
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Layout
          </label>
          <select
            value={layout}
            onChange={(e) => handleChange('layout', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="centered">Centered</option>
            <option value="left">Left-aligned</option>
            <option value="right">Right-aligned</option>
            <option value="split">Split</option>
            <option value="grid">Grid</option>
          </select>
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Background Color
          </label>
          <div className="flex items-center">
            <input
              type="color"
              value={backgroundColor}
              onChange={(e) => handleChange('backgroundColor', e.target.value)}
              className="w-10 h-10 border border-gray-300 rounded-md mr-2"
            />
            <input
              type="text"
              value={backgroundColor}
              onChange={(e) => handleChange('backgroundColor', e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Text Color
          </label>
          <div className="flex items-center">
            <input
              type="color"
              value={textColor}
              onChange={(e) => handleChange('textColor', e.target.value)}
              className="w-10 h-10 border border-gray-300 rounded-md mr-2"
            />
            <input
              type="text"
              value={textColor}
              onChange={(e) => handleChange('textColor', e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>
      
      {/* Preview */}
      <div className="w-2/3 p-4 bg-gray-50 flex items-center justify-center overflow-y-auto">
        <div className="w-full max-w-3xl">
          <div className="mb-4 text-center">
            <h3 className="text-gray-700">Slide Preview</h3>
          </div>
          
          <SlidePreview 
            slide={{
              ...slide,
              title,
              content,
              imageUrl: imageUrl || undefined,
              style: {
                ...slide.style,
                backgroundColor,
                textColor,
                layout: layout as any,
              }
            }}
            theme={theme}
          />
        </div>
      </div>
    </div>
  );
};

export default SlideEditor;
